import { useCallback, useEffect, useState } from 'react';

import { usePrevious } from 'react-use';
import { useShallow } from 'zustand/react/shallow';

import { useNftDataStream, usePrepareOrder } from '@pkg/hooks';
import { getDateFromNow } from '@pkg/hooks/datetimeHooks/useExpireTimestamp/useExpireTimestamp';
import { type BaseCurrencyType } from '@pkg/types';
import { assertNumber, convertWeiToEth } from '@pkg/utils';

import { Currency } from '@app/client';
import { useExchangeRatesStore, useOrdersStore } from '@app/store/zustand';

import { type InputPriceType } from './AssetOrderSheet.types';
import { filterInputPrice, formatInputPrice } from './AssetOrderSheet.utilities';

export function useAssetOrderState() {
    const { token, setPendingConfirmation } = useOrdersStore(useShallow((state) => state));
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);
    const { prepareOrder: confirmOrder } = usePrepareOrder();
    const assetId = token?.id || '';
    const [_chain, contractAddress] = assetId.split('.');

    const { loading, nativeCurrency, appraisal, floorData, lastSaleData, errors } = useNftDataStream({
        contractAddress,
        tokenId: token?.tokenId,
        disabled: _chain === 'aptos',
    });

    // in case user doesn't select anything from dropdown
    const initialExpiry = getDateFromNow('week').toISOString();

    const [localCurrency, setLocalCurrency] = useState<BaseCurrencyType>('usd');
    const [expiration, setExpiration] = useState(initialExpiry);
    const [allMarketplaces, setAllMarketplaces] = useState(false);
    // TODO: Waiting on backend before these features can be completed.
    const [creatorFeeUsd, _setCreatorFeeUsd] = useState(0);
    const [processFeeUsd, _setProcessFeeUsd] = useState(0);
    const [estimationUsd, setEstimationUsd] = useState(0);

    const prevLocalCurrency = usePrevious(localCurrency);

    const [localPrice, setLocalPrice] = useState<InputPriceType>();
    const [nativePrice, setNativePrice] = useState<number>();
    const [usdPrice, setUsdPrice] = useState(0);
    const [localAppraisal, setLocalAppraisal] = useState<number>(0);

    const appraisedEth = appraisal.ethValue;

    const formatAndUpdateLocalPrice = useCallback((value?: InputPriceType, currencyType: BaseCurrencyType = 'usd') => {
        const newValue = formatInputPrice(value, currencyType);
        setLocalPrice(newValue);
    }, []);

    function handleSetExpiration(value: string) {
        setExpiration(value);
    }
    function handlePriceChange(value?: InputPriceType) {
        setLocalPrice(value);
    }
    function handleAllMarketplacesToggle() {
        setAllMarketplaces((current) => !current);
    }

    const handleSubmit = useCallback(async () => {
        if (nativePrice) {
            setPendingConfirmation(true);
            await confirmOrder({ confirmedPrice: nativePrice, expiration, allMarkets: allMarketplaces });
        }
    }, [allMarketplaces, confirmOrder, expiration, nativePrice, setPendingConfirmation]);
    // Tracking a constant usdPrice conversion.
    useEffect(() => {
        const newValue = assertNumber(localPrice) ? convertCurrency(localCurrency, 'usd', localPrice, true) : 0;

        setUsdPrice(newValue + creatorFeeUsd + processFeeUsd);
    }, [convertCurrency, localCurrency, localPrice, creatorFeeUsd, processFeeUsd]);
    // Tracking conversion of localAppraisal.
    useEffect(() => {
        setLocalAppraisal(convertCurrency(nativeCurrency, localCurrency, appraisedEth, true));
    }, [convertCurrency, nativeCurrency, localCurrency, appraisedEth]);

    // Tracking conversion of localPrice when localCurrency is changed.
    useEffect(() => {
        if (assertNumber(localPrice) && prevLocalCurrency && prevLocalCurrency !== localCurrency) {
            const convertedPrice = convertCurrency(prevLocalCurrency, localCurrency, localPrice, true);

            formatAndUpdateLocalPrice(convertedPrice, localCurrency);
        }
    }, [convertCurrency, prevLocalCurrency, localCurrency, localPrice, formatAndUpdateLocalPrice]);

    // Tracking conversion of nativePrice when localPrice updates.
    useEffect(() => {
        const filtered = filterInputPrice(localPrice);
        const fromCurrency =
            prevLocalCurrency && prevLocalCurrency !== localCurrency ? prevLocalCurrency : localCurrency;
        const newValue =
            filtered === undefined ? undefined : convertCurrency(fromCurrency, nativeCurrency, filtered, true);
        setNativePrice(newValue);
    }, [convertCurrency, prevLocalCurrency, localCurrency, nativeCurrency, localPrice]);

    // Tracking estimationUsd as requisite values update.
    useEffect(() => {
        setEstimationUsd(usdPrice + creatorFeeUsd + processFeeUsd);
    }, [usdPrice, creatorFeeUsd, processFeeUsd]);

    // TODO: Check with BE about prices in response.
    const floorPrice = Number(token?.collection?.floorPrice);
    const ethFloorPriceValue = convertWeiToEth(floorPrice);

    const usdFloorPriceValue = convertCurrency(Currency.ETH, Currency.USD, ethFloorPriceValue);
    const currency = _chain === 'aptos' ? 'apt' : _chain === 'polygon' ? 'matic' : nativeCurrency;
    return {
        errors,
        loading,
        token,
        nativeCurrency: currency,
        floorUsdValue: floorData.usdValue || usdFloorPriceValue,
        floorUsd24hChange: floorData.usd24hChange,
        floorPercentChange: floorData.percentChange,
        lastSaleUsd: lastSaleData.usdValue,
        lastSaleUtcMs: lastSaleData.utcMs,
        localCurrency,
        localAppraisal,
        localPrice,
        nativePrice,
        usdPrice,
        appraisedEth,
        formatAndUpdateLocalPrice,
        handlePriceBlur: () => formatAndUpdateLocalPrice(localPrice, localCurrency),
        handlePriceChange,
        handleLocalCurrencyChange: setLocalCurrency,
        handleSubmit,
        allMarketplaces,
        handleAllMarketplacesToggle,
        onSetExpiration: handleSetExpiration,
        creatorFeeUsd,
        processFeeUsd,
        estimationUsd,
    };
}
